import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  NavBar,
  Button,
  Tag,
  Card,
  Toast,
  Dialog,
  Modal,
  TextArea,
  Space,
  SpinLoading,
  Empty,
  ImageViewer,
} from 'antd-mobile';
import { EyeOutline } from 'antd-mobile-icons';
import { zavhozApi } from '../api';
import { ZAVHOZ_STATUSES, zavhozActionLabel } from './zavhozMeta';
import dayjs from 'dayjs';

function fmtDate(value) {
  return value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '';
}

function PhotoGrid({ urls, onOpen }) {
  if (!urls.length) return null;
  return (
    <Space wrap>
      {urls.map((url, idx) => (
        <div
          key={idx}
          onClick={() => onOpen(idx)}
          style={{ position: 'relative', width: 88, height: 88 }}
        >
          <img
            src={url}
            alt={`photo-${idx}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 24,
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '0 0 10px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EyeOutline style={{ color: '#fff', fontSize: 14 }} />
          </div>
        </div>
      ))}
    </Space>
  );
}

function ZavhozRequestPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const [returnVisible, setReturnVisible] = useState(false);
  const [returnComment, setReturnComment] = useState('');

  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await zavhozApi.detail(id);
      setRequest(res.data);
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка загрузки заявки' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const photoUrls = useMemo(() => {
    if (!request) return [];
    if (Array.isArray(request.photos) && request.photos.length) {
      return request.photos.map((p) => p.s3_url);
    }
    return request.photo_urls || [];
  }, [request]);

  const completionUrls = request?.completion_photo_urls || [];

  const feed = useMemo(() => {
    if (!request) return [];
    const items = [];
    (request.comments || []).forEach((c) => {
      items.push({ kind: 'comment', id: `c-${c.id}`, created_at: c.created_at, data: c });
    });
    (request.history || []).forEach((h) => {
      items.push({ kind: 'history', id: `h-${h.id}`, created_at: h.created_at, data: h });
    });
    return items.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
  }, [request]);

  const openPreview = (images, idx) => {
    setPreviewImages(images);
    setPreviewIndex(idx);
    setPreviewVisible(true);
  };

  const handleError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    Toast.show({ icon: 'fail', content: typeof detail === 'string' ? detail : fallback });
  };

  const runAction = async (fn, successText, fallback) => {
    setActionLoading(true);
    try {
      await fn();
      Toast.show({ icon: 'success', content: successText });
      await loadDetail();
    } catch (err) {
      handleError(err, fallback);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    const ok = await Dialog.confirm({
      content: 'Подтвердить выполнение заявки?',
      confirmText: 'Подтвердить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    runAction(() => zavhozApi.confirm(id), 'Выполнение подтверждено', 'Ошибка подтверждения');
  };

  const handleReturn = () => {
    if (!returnComment.trim()) {
      Toast.show({ icon: 'fail', content: 'Укажите, что не устранено' });
      return;
    }
    setReturnVisible(false);
    runAction(
      () => zavhozApi.returnRequest(id, returnComment.trim()),
      'Заявка возвращена в работу',
      'Ошибка возврата заявки'
    );
    setReturnComment('');
  };

  const handleCancel = async () => {
    const ok = await Dialog.confirm({
      content: 'Отменить заявку?',
      confirmText: 'Да, отменить',
      cancelText: 'Нет',
    });
    if (!ok) return;
    runAction(() => zavhozApi.cancel(id), 'Заявка отменена', 'Ошибка отмены заявки');
  };

  const handleSendComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setSendingComment(true);
    try {
      await zavhozApi.addComment(id, text);
      setCommentText('');
      await loadDetail();
    } catch (err) {
      handleError(err, 'Ошибка отправки комментария');
    } finally {
      setSendingComment(false);
    }
  };

  if (loading && !request) {
    return (
      <div style={{ padding: '0 12px 20px' }}>
        <NavBar
          onBack={() => navigate(-1)}
          style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
        >
          Заявка
        </NavBar>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <SpinLoading />
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ padding: '0 12px 20px' }}>
        <NavBar
          onBack={() => navigate(-1)}
          style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
        >
          Заявка
        </NavBar>
        <Empty description="Заявка не найдена" style={{ paddingTop: 40 }} />
      </div>
    );
  }

  const status = ZAVHOZ_STATUSES[request.status] || { label: request.status, color: 'default' };

  return (
    <div style={{ padding: '0 12px 100px' }}>
      <NavBar
        onBack={() => navigate(-1)}
        style={{ background: '#1890ff', color: '#fff', margin: '0 -12px' }}
      >
        Заявка №{request.id}
      </NavBar>

      <Card style={{ marginTop: 16, borderRadius: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: 1.4, flex: 1, minWidth: 0 }}>
            {request.description}
          </div>
          <Tag color={status.color} style={{ flexShrink: 0 }}>
            {status.label}
          </Tag>
        </div>
        {request.category && (
          <div style={{ fontSize: 13, color: '#8c8c8c', marginTop: 8 }}>
            Категория: {request.category}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#bfbfbf', marginTop: 8 }}>
          Создана: {fmtDate(request.created_at)}
          {request.initiator_name ? ` · ${request.initiator_name}` : ''}
          {request.initiator_department ? ` (${request.initiator_department})` : ''}
        </div>
        {request.executor_name && (
          <div style={{ fontSize: 13, color: '#595959', marginTop: 6 }}>
            Исполнитель: {request.executor_name}
          </div>
        )}
        {request.completed_at && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
            Выполнена: {fmtDate(request.completed_at)}
          </div>
        )}
        {request.confirmed_at && (
          <div style={{ fontSize: 12, color: '#52c41a', marginTop: 6 }}>
            Подтверждена: {fmtDate(request.confirmed_at)}
          </div>
        )}
      </Card>

      {photoUrls.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
            Фото заявителя ({photoUrls.length})
          </div>
          <PhotoGrid urls={photoUrls} onOpen={(idx) => openPreview(photoUrls, idx)} />
        </div>
      )}

      {completionUrls.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
            Фото выполнения ({completionUrls.length})
          </div>
          <PhotoGrid urls={completionUrls} onOpen={(idx) => openPreview(completionUrls, idx)} />
        </div>
      )}

      {request.status === 'completed' && (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button
            block
            color="primary"
            loading={actionLoading}
            onClick={handleConfirm}
            style={{ borderRadius: 12, height: 44 }}
          >
            Подтвердить выполнение
          </Button>
          <Button
            block
            color="danger"
            fill="outline"
            loading={actionLoading}
            onClick={() => setReturnVisible(true)}
            style={{ borderRadius: 12, height: 44 }}
          >
            Не устранено
          </Button>
        </div>
      )}

      {request.status === 'new' && (
        <div style={{ marginTop: 24 }}>
          <Button
            block
            color="danger"
            fill="outline"
            loading={actionLoading}
            onClick={handleCancel}
            style={{ borderRadius: 12, height: 44 }}
          >
            Отменить заявку
          </Button>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14, color: '#595959' }}>
          Обсуждение
        </div>
        {feed.length === 0 ? (
          <div style={{ fontSize: 13, color: '#8c8c8c', padding: '8px 0' }}>
            Пока нет комментариев и событий
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {feed.map((item) =>
              item.kind === 'comment' ? (
                <div
                  key={item.id}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>
                      {item.data.author_name}
                    </div>
                    <div style={{ fontSize: 11, color: '#bfbfbf', flexShrink: 0 }}>
                      {fmtDate(item.created_at)}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: '#595959', marginTop: 6, lineHeight: 1.4 }}>
                    {item.data.text}
                  </div>
                </div>
              ) : (
                <div
                  key={item.id}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 12,
                    background: '#f5f5f5',
                    fontSize: 12,
                    color: '#8c8c8c',
                  }}
                >
                  {zavhozActionLabel(item.data.action)}
                  {item.data.actor_name ? ` — ${item.data.actor_name}` : ''}
                  {item.data.description ? ` · ${item.data.description}` : ''}
                  <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>
                    {fmtDate(item.created_at)}
                  </div>
                </div>
              )
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-end' }}>
          <TextArea
            placeholder="Написать комментарий…"
            value={commentText}
            onChange={setCommentText}
            rows={2}
            maxLength={500}
            style={{
              flex: 1,
              background: '#fff',
              borderRadius: 12,
              padding: 10,
              border: '1px solid #f0f0f0',
            }}
          />
          <Button
            color="primary"
            loading={sendingComment}
            disabled={!commentText.trim()}
            onClick={handleSendComment}
            style={{ borderRadius: 12, flexShrink: 0 }}
          >
            Отправить
          </Button>
        </div>
      </div>

      <Modal
        visible={returnVisible}
        title="Что не устранено?"
        content={
          <TextArea
            placeholder="Опишите, что осталось невыполненным"
            value={returnComment}
            onChange={setReturnComment}
            rows={3}
            maxLength={500}
            style={{
              background: '#f5f5f5',
              borderRadius: 8,
              padding: 10,
              marginTop: 8,
            }}
          />
        }
        closeOnAction
        onClose={() => setReturnVisible(false)}
        actions={[
          { key: 'cancel', text: 'Отмена' },
          { key: 'send', text: 'Вернуть в работу', danger: true, onClick: handleReturn },
        ]}
      />

      <ImageViewer.Multi
        images={previewImages}
        visible={previewVisible}
        defaultIndex={previewIndex}
        onClose={() => setPreviewVisible(false)}
      />
    </div>
  );
}

export default ZavhozRequestPage;
